import type { UserRole } from './auth.js';
import type { AssetStatus, AssetType, ConsumableUnit, PublicAsset, PublicTote } from './catalog.js';

/** Inventory sessions (F-05, ADR-005, ADR-006, ADR-007, DATA-MODEL.md). */

export const INVENTORY_STAGES = [
  'shop_pack',
  'venue_receive',
  'venue_pack',
  'shop_receive',
] as const;
export type InventoryStage = (typeof INVENTORY_STAGES)[number];

export const INVENTORY_STAGE_LABELS: Record<InventoryStage, string> = {
  shop_pack: 'Shop pack',
  venue_receive: 'Venue receive',
  venue_pack: 'Venue pack-out',
  shop_receive: 'Shop receive',
};

export const INVENTORY_LOCATION_KINDS = ['shop', 'event'] as const;
export type InventoryLocationKind = (typeof INVENTORY_LOCATION_KINDS)[number];

export const SESSION_STATUSES = ['in_progress', 'submitted', 'finalized', 'void'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export const SESSION_STATUS_LABELS: Record<SessionStatus, string> = {
  in_progress: 'In progress',
  submitted: 'Submitted',
  finalized: 'Finalized',
  void: 'Void',
};

export const LINE_RESULTS = ['present', 'missing', 'broken', 'extra'] as const;
export type LineResult = (typeof LINE_RESULTS)[number];

export const LINE_RESULT_LABELS: Record<LineResult, string> = {
  present: 'Present',
  missing: 'Missing',
  broken: 'Broken',
  extra: 'Extra',
};

export const DEFAULT_LOCK_TIMEOUT_MINUTES = 30;
export const INVENTORY_HEARTBEAT_MS = 60_000;

export function isInventoryStage(value: unknown): value is InventoryStage {
  return typeof value === 'string' && (INVENTORY_STAGES as readonly string[]).includes(value);
}

export function isInventoryLocationKind(value: unknown): value is InventoryLocationKind {
  return (
    typeof value === 'string' && (INVENTORY_LOCATION_KINDS as readonly string[]).includes(value)
  );
}

export function isSessionStatus(value: unknown): value is SessionStatus {
  return typeof value === 'string' && (SESSION_STATUSES as readonly string[]).includes(value);
}

export function isLineResult(value: unknown): value is LineResult {
  return typeof value === 'string' && (LINE_RESULTS as readonly string[]).includes(value);
}

export function isVenueStage(stage: InventoryStage): boolean {
  return stage === 'venue_receive' || stage === 'venue_pack';
}

export function isShopStage(stage: InventoryStage): boolean {
  return stage === 'shop_pack' || stage === 'shop_receive';
}

export function locationKindFor(stage: InventoryStage): InventoryLocationKind {
  return isShopStage(stage) ? 'shop' : 'event';
}

/** Admin, equipment admin, and staff inventory assigned totes. Viewers do not. TD codes are F-08. */
export function roleCanInventory(role: UserRole): boolean {
  return role === 'admin' || role === 'equipment_admin' || role === 'staff';
}

export function roleCanForceUnlock(role: UserRole): boolean {
  return role === 'admin' || role === 'equipment_admin';
}

export function roleCanOverrideInventory(role: UserRole): boolean {
  return role === 'admin' || role === 'equipment_admin';
}

export interface PublicInventoryLockHolder {
  userId: string | null;
  codeId: string | null;
  name: string;
}

export interface PublicInventoryLock {
  held: boolean;
  expired: boolean;
  holder: PublicInventoryLockHolder | null;
  heartbeatAt: string | null;
  lockedAt: string | null;
  expiresAt: string | null;
  youHoldIt: boolean;
}

export interface PublicInventoryLinePhoto {
  id: string;
  lineId: string;
  createdAt: string;
}

export interface PublicInventoryLine {
  id: string;
  sessionId: string;
  assetId: string | null;
  asset: PublicAsset | null;
  serial: string | null;
  assetType: AssetType | null;
  assetStatus: AssetStatus | null;
  consumableTypeId: string | null;
  consumableName: string | null;
  consumableUnit: ConsumableUnit | null;
  expected: boolean;
  result: LineResult | null;
  qtyExpected: number | null;
  qtyPresent: number | null;
  notes: string | null;
  updatedAt: string;
  updatedBy: string | null;
  updatedByCodeId: string | null;
  updatedByName: string | null;
  photos: PublicInventoryLinePhoto[];
}

export interface PublicInventoryLoadoutOption {
  id: string;
  name: string | null;
  events: { id: string; name: string; startDate: string; endDate: string }[];
}

export interface PublicInventorySession {
  id: string;
  toteId: string;
  tote: PublicTote;
  loadoutId: string | null;
  loadout: PublicInventoryLoadoutOption | null;
  locationKind: InventoryLocationKind;
  stage: InventoryStage;
  status: SessionStatus;
  version: number;
  notes: string | null;
  submittedAt: string | null;
  finalizedAt: string | null;
  finalizedBy: string | null;
  lock: PublicInventoryLock;
  lines: PublicInventoryLine[];
  membershipUnchanged: true;
}

export interface InventorySessionListResponse {
  tote: PublicTote;
  loadouts: PublicInventoryLoadoutOption[];
  sessions: PublicInventorySession[];
  lock: PublicInventoryLock | null;
  lockTimeoutMinutes: number;
}

export interface InventorySessionResponse {
  session: PublicInventorySession;
}

export interface InventoryLinePhotoResponse {
  photo: PublicInventoryLinePhoto;
}
